import { UserStatus } from 'app/App';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { getRouteToLoadAfterAuth, setRouteToLoadAfterAuth } from 'app/slices/AppUiSlice';
import { getSelectedSeasonWeek } from 'app/slices/GamesSlice';
import { setSelectedScene } from 'app/slices/NavigationSlice';
import { getBESyncSeasons } from 'app/slices/SyncManagerSlice';
import { getUserAuthState, UserAuthState } from 'app/slices/UserSlice';
import { toWeekID } from '@shared/helpers';
import { breakSeasonWeekID } from 'helpers/IdHelpers';
import React, { useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate, useNavigation } from 'react-router-dom';
import { getDestinationAfterAuth, getNavigationTarget } from 'routes/navigation';
import { AppRoutes } from 'routes/Routes';
import { AppScene } from 'types';

interface NavigationManagerProps {
  className?: string;
  userStatus?: UserStatus;
}

const NavigationManager: React.FC<NavigationManagerProps> = ({ userStatus }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const savedRound = useAppSelector(getRouteToLoadAfterAuth);
  const authState = useAppSelector(getUserAuthState);
  const location = useLocation();
  const navigation = useNavigation();
  const seasonWeekID = useAppSelector(getSelectedSeasonWeek);

  const seasons = useAppSelector(getBESyncSeasons);
  const availableSeasons = useMemo(
    () =>
      authState === UserAuthState.LoggedIn && seasons.length
        ? seasons.map(({ seasonID }) => seasonID)
        : undefined,
    [authState, seasons],
  );
  const previousSeasonWeek = useRef(seasonWeekID);
  const restoredDestination = useRef<string | null>(null);
  const target = getNavigationTarget(location.pathname, availableSeasons);

  useEffect(() => {
    if (target.scene !== undefined) {
      dispatch(setSelectedScene(target.scene));
    }
  }, [target.scene, dispatch]);

  useEffect(() => {
    const changed = previousSeasonWeek.current !== seasonWeekID;
    previousSeasonWeek.current = seasonWeekID;
    // Route loaders also update selection. They must not create a second navigation
    // while a direct link or Back/Forward transition is already being resolved.
    if (!changed || navigation.state !== 'idle' || authState !== UserAuthState.LoggedIn) return;
    const route =
      target.scene === AppScene.WeeklyGames
        ? AppRoutes.WeeklyGames
        : target.scene === AppScene.CategoryRankings
          ? AppRoutes.CategoryRankings
          : undefined;
    if (!route) return;
    const { yearID, weekID } = breakSeasonWeekID(seasonWeekID);
    const newPath = `${route}/${yearID}/${toWeekID(weekID).toLowerCase()}`;
    const [, , routeYear, routeWeek] = location.pathname.split('/');
    if (routeYear === yearID && routeWeek && toWeekID(routeWeek) === toWeekID(weekID)) return;
    navigate(`${newPath}${location.search}${location.hash}`);
  }, [
    seasonWeekID,
    authState,
    target.scene,
    location.pathname,
    location.search,
    location.hash,
    navigation.state,
    navigate,
  ]);

  useEffect(() => {
    if ([UserAuthState.Init, UserAuthState.Authenticating].includes(authState)) return;
    const currentDestination = `${location.pathname}${location.search}${location.hash}`;
    if (restoredDestination.current === currentDestination && availableSeasons) {
      restoredDestination.current = null;
      if (target.kind === 'not-found') {
        navigate(getDestinationAfterAuth(currentDestination, availableSeasons), { replace: true });
        return;
      }
    }
    // Index expansion may use the persisted year before season metadata is available.
    // Retry the index with known seasons; explicit invalid deep links still show NotFound.
    if (
      authState === UserAuthState.LoggedIn &&
      availableSeasons &&
      location.state?.validateSelectedSeason &&
      target.kind === 'not-found'
    ) {
      navigate(`${AppRoutes.WeeklyGames}${location.search}${location.hash}`, { replace: true });
      return;
    }
    if (target.kind === 'not-found') return;
    if (authState !== UserAuthState.LoggedIn) {
      if (target.kind === 'restricted') {
        dispatch(setRouteToLoadAfterAuth(`${location.pathname}${location.search}${location.hash}`));
        navigate(AppRoutes.Home, { replace: true });
      }
      return;
    }
    if (target.scene === AppScene.Authentication) {
      // Wait for the existing app initialization before validating season availability.
      if (!userStatus?.success) return;
      const destination = getDestinationAfterAuth(savedRound, availableSeasons);
      restoredDestination.current = availableSeasons ? null : destination;
      dispatch(setRouteToLoadAfterAuth(AppRoutes.Home));
      navigate(destination, { replace: true });
    }
  }, [
    authState,
    target.kind,
    target.scene,
    location.pathname,
    location.search,
    location.hash,
    location.state,
    savedRound,
    availableSeasons,
    userStatus?.success,
    dispatch,
    navigate,
  ]);

  // This component doesn't render anything - it's just for navigation logic
  return null;
};

export default NavigationManager;
